/**
 * CdmSelectionList - Interactive CDM card list for maneuver planning workflow
 *
 * Renders a list of CDM summary cards inline in the chat message flow.
 * Users click a card to select it, then click "Plan Maneuver" to trigger
 * the collision avoidance planning workflow.
 *
 * Workflow: List CDMs -> Select one -> Plan maneuver -> View results
 *
 * Retro mission control aesthetic with high information density.
 */

import { useState } from 'react'
import { Rocket } from 'lucide-react'
import CdmCard from './CdmCard'
import type { CdmSummary } from '../../types/cdm'

interface CdmSelectionListProps {
  cdms: CdmSummary[]
  onPlanManeuver: (cdmId: string) => void
  disabled?: boolean
}

function CdmSelectionList({ cdms, onPlanManeuver, disabled = false }: CdmSelectionListProps) {
  const [selectedCdmId, setSelectedCdmId] = useState<string | null>(null)

  if (cdms.length === 0) return null

  const handleSelect = (cdmId: string) => {
    setSelectedCdmId((prev) => (prev === cdmId ? null : cdmId))
  }

  const handlePlanManeuver = () => {
    if (selectedCdmId && !disabled) {
      onPlanManeuver(selectedCdmId)
    }
  }

  return (
    <div className="my-3">
      {/* Header — action on LHS so the primary CTA reads first */}
      <div className="flex items-center justify-between mb-3">
        {selectedCdmId ? (
          <button
            type="button"
            onClick={handlePlanManeuver}
            disabled={disabled}
            className={`
              inline-flex items-center gap-2 px-3 py-1.5
              font-mono text-xs font-bold uppercase tracking-wider
              border rounded transition-all duration-200
              ${disabled
                ? 'border-retro-dim text-retro-dim cursor-not-allowed opacity-50'
                : 'border-retro-amber text-retro-amber hover:bg-retro-amber hover:text-retro-bg active:scale-95'
              }
            `}
          >
            <Rocket size={12} />
            PLAN MANEUVER
          </button>
        ) : (
          <span className="text-xs font-mono text-retro-dim italic">
            Select a conjunction to plan a maneuver
          </span>
        )}

        <div className="flex items-center gap-2">
          <div
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: '#ffcc66' }}
          />
          <span className="text-xs font-mono uppercase tracking-wider text-retro-amber font-bold">
            {cdms.length} Conjunction{cdms.length !== 1 ? 's' : ''} Found
          </span>
        </div>
      </div>

      {/* CDM Cards */}
      <div className="space-y-2">
        {cdms.map((cdm) => (
          <CdmCard
            key={cdm.cdm_id}
            cdm={cdm}
            onSelect={handleSelect}
            isSelected={selectedCdmId === cdm.cdm_id}
          />
        ))}
      </div>

      {/* Selected CDM summary */}
      {selectedCdmId && (
        <div className="mt-3 px-3 py-2 rounded border border-retro-border bg-retro-bg">
          <span className="text-xs font-mono text-retro-cyan">
            Selected: {selectedCdmId}
          </span>
        </div>
      )}
    </div>
  )
}

export default CdmSelectionList
